// Eligibility tab — formerly the standalone EligibilityDetailDrawer's body.
// Hosts the same eligibility content (summary, verification form, alerts,
// financials, coverage, history) but lives inside the unified drawer.
// Skipped from the old drawer per design decision: silence menu, lock
// accent border, dismissable cards, resize handle (chrome concerns owned
// by the SlideSidePane wrapper, which we're keeping vanilla here).
//
// The "Log collection" affordance flips the drawer to the Ledger tab and
// opens the settle sheet (method=external) — no second drawer, no swap.

import { useEffect, useState } from 'react';
import {
  EligibilityAlertsBlock,
  EligibilityCoverageBlock,
  EligibilityFinancialsBlock,
  type EligibilitySource,
  EligibilitySummaryContent,
  VerificationForm,
  VerificationHistorySection,
} from '@features/eligibility-checks';
import { useEligibilityBadgeState } from '@features/eligibility-checks/hooks/useEligibilityBadgeState';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import { Box, Button, Divider, Skeleton, Stack } from '@mui/material';
import { useEventTracking } from '@services/statsig';

interface Props {
  patientId: string;
  encounterId?: string;
  patientName?: string | null;
  source?: EligibilitySource | (string & {});
  /** Auto-expand the verification form on mount (worklist row click for blocked/attention). */
  initialForm?: 'verification';
  /** Flip to Ledger tab + open settle sheet pre-targeted to method=external. */
  onLogCollection: () => void;
  canRunCheck?: boolean;
  canLogCollection?: boolean;
}

export const EligibilityTabContent = ({
  patientId,
  encounterId,
  patientName,
  source = 'patient_overview',
  initialForm,
  onLogCollection,
  canRunCheck = true,
  canLogCollection = true,
}: Props) => {
  const { eligibilityChecks } = useEventTracking();
  const { eligibilityData, isLoading } = useEligibilityBadgeState({ patientId, encounterId });
  const [openForm, setOpenForm] = useState<'verification' | null>(initialForm ?? null);

  const isSelfPay = Boolean(eligibilityData?.is_self_pay);
  const typedSource = source as EligibilitySource;

  const openVerification = (trigger: 'verify_button' | 're_verify_link') => {
    eligibilityChecks.verificationFormOpened?.({
      patient_id: patientId,
      encounter_id: encounterId,
      source: typedSource,
      trigger,
    });
    setOpenForm('verification');
  };

  const openCollection = () => {
    eligibilityChecks.collectionFormOpened?.({
      patient_id: patientId,
      encounter_id: encounterId,
      source: typedSource,
      readiness: eligibilityData?.readiness ?? undefined,
    });
    onLogCollection();
  };

  const closeForm = () => setOpenForm(null);

  // Mount-time auto-expand. Triggered when the worklist row click for
  // blocked / attention / expired states lands the user on this tab and
  // we want the verification form already open.
  useEffect(() => {
    if (initialForm === 'verification') setOpenForm('verification');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading && !eligibilityData && openForm !== 'verification') {
    return <EligibilityDetailSkeleton />;
  }

  return (
    <Box>
      <EligibilitySummaryContent
        patientId={patientId}
        encounterId={encounterId}
        patientName={patientName ?? undefined}
        source={typedSource}
        showRerun={!isSelfPay && canRunCheck}
        hideAlerts
        hideProvenance={isSelfPay}
        // Verification + log-collection paths are owned by VerificationArea
        // below — without this the summary's secondary action would open a
        // duplicate flow.
        hideSecondaryAction
      />

      {!isSelfPay && (
        <>
          <VerificationArea
            patientId={patientId}
            encounterId={encounterId}
            source={typedSource}
            eligibilityData={eligibilityData}
            canRunCheck={canRunCheck}
            canLogCollection={canLogCollection}
            openForm={openForm}
            onOpenVerification={openVerification}
            onOpenCollection={openCollection}
            onCloseForm={closeForm}
          />
          <Divider sx={{ my: 3, borderColor: ELIG_TOKENS.line }} />
          <EligibilityAlertsBlock patientId={patientId} encounterId={encounterId} variant="full" />
          <EligibilityFinancialsBlock patientId={patientId} encounterId={encounterId} />
          <EligibilityCoverageBlock patientId={patientId} encounterId={encounterId} />
          <VerificationHistorySection
            patientId={patientId}
            encounterId={encounterId}
            source={typedSource}
          />
        </>
      )}
    </Box>
  );
};

const EligibilityDetailSkeleton = () => (
  <Box role="status" aria-label="Loading eligibility details" aria-live="polite">
    <Stack spacing={1.25} aria-hidden>
      <Skeleton width={112} height={14} />
      <Box>
        <Skeleton width={188} height={28} />
        <Skeleton width="42%" height={20} />
      </Box>
      <Skeleton variant="rounded" width={128} height={36} />
      <Skeleton width={196} height={18} />
    </Stack>

    <Divider sx={{ my: 3, borderColor: ELIG_TOKENS.line }} />

    <DetailSectionSkeleton hero />
    <DetailSectionSkeleton rows={4} />
  </Box>
);

const DetailSectionSkeleton = ({ hero = false, rows = 0 }: { hero?: boolean; rows?: number }) => (
  <Box sx={{ mb: 3 }} aria-hidden>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
      <Skeleton width={72} height={18} />
      <Skeleton width={132} height={18} />
    </Box>
    {hero ? <Skeleton width={156} height={52} /> : null}
    {Array.from({ length: rows }, (_, index) => (
      <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
        <Skeleton width={96 + index * 12} height={22} />
        <Skeleton width={72} height={22} />
      </Box>
    ))}
  </Box>
);

// ─── VerificationArea ────────────────────────────────────────────────────────
// Verification form OR verify/log-collection buttons. Lifted from the old
// EligibilityDetailDrawer. The lock-active branch is dropped per design
// decision (lock accent / provenance live elsewhere now); when a lock is
// active the user still sees the verify-benefits affordance and can re-verify
// from inside the verification form itself.

interface VerificationAreaProps {
  patientId: string;
  encounterId?: string;
  source: EligibilitySource;
  eligibilityData: ReturnType<typeof useEligibilityBadgeState>['eligibilityData'];
  canRunCheck: boolean;
  canLogCollection: boolean;
  openForm: 'verification' | null;
  onOpenVerification: (trigger: 'verify_button' | 're_verify_link') => void;
  onOpenCollection: () => void;
  onCloseForm: () => void;
}

const VerificationArea = ({
  patientId,
  encounterId,
  source,
  eligibilityData,
  canRunCheck,
  canLogCollection,
  openForm,
  onOpenVerification,
  onOpenCollection,
  onCloseForm,
}: VerificationAreaProps) => {
  if (openForm === 'verification') {
    return (
      <VerificationForm
        patientId={patientId}
        encounterId={encounterId}
        source={source}
        onClose={onCloseForm}
      />
    );
  }

  if (!eligibilityData) return null;
  if (!eligibilityData.has_coverage || eligibilityData.is_self_pay) return null;
  if (!canRunCheck && !canLogCollection) return null;

  // Lead with whichever path matches the user's likely intent.
  const collectionLeads = eligibilityData.readiness === 'collect';

  return (
    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {collectionLeads ? (
        <>
          {canLogCollection ? (
            <PrimaryAffordance label="Log collection →" onClick={onOpenCollection} />
          ) : null}
          {canRunCheck ? (
            <SecondaryAffordance
              label="Verify benefits →"
              onClick={() => onOpenVerification('verify_button')}
            />
          ) : null}
        </>
      ) : (
        <>
          {canRunCheck ? (
            <PrimaryAffordance
              label="Verify benefits →"
              onClick={() => onOpenVerification('verify_button')}
            />
          ) : null}
          {canLogCollection ? (
            <SecondaryAffordance label="Log collection →" onClick={onOpenCollection} />
          ) : null}
        </>
      )}
    </Box>
  );
};

const PrimaryAffordance = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <Button
    variant="outlined"
    size="small"
    onClick={onClick}
    sx={{
      textTransform: 'none',
      fontWeight: 500,
      borderColor: ELIG_TOKENS.line,
      color: ELIG_TOKENS.midnight,
      '&:hover': {
        borderColor: ELIG_TOKENS.lineStrong,
        bgcolor: 'rgba(28,30,133,0.04)',
      },
    }}
  >
    {label}
  </Button>
);

const SecondaryAffordance = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <Button
    variant="text"
    size="small"
    onClick={onClick}
    sx={{
      textTransform: 'none',
      fontWeight: 500,
      color: ELIG_TOKENS.inkSoft,
      '&:hover': { color: ELIG_TOKENS.midnight, bgcolor: 'transparent' },
    }}
  >
    {label}
  </Button>
);
